// Heartbeats (architecture sprint-14 §8.2, B-5): while the person is on an alarm shift, every tab tells the server
// it is open, whether its audio is armed and whether it is visible: on mount, every 60 s, on visibilitychange and
// on armed change. The server throttles writes; errors are silent (the next beat retries).
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { isArmed, onArmedChange } from '@/lib/alarm-audio';
import { currentDevice } from '@/lib/device';
import { postHeartbeat, setAlarmSnapshot } from './use-alarm';

export const HEARTBEAT_INTERVAL_MS = 60_000;

export function useAlarmHeartbeat(active: boolean): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const beat = (): void => {
      const body = { visible: document.visibilityState === 'visible', armed: isArmed(), device: currentDevice() };
      postHeartbeat(body)
        .then((res) => {
          if (!cancelled) setAlarmSnapshot(queryClient, res.alarm);
        })
        .catch(() => undefined);
    };
    beat();
    // setInterval is fine here: it only runs while a tab is open, which is exactly what it reports.
    const id = window.setInterval(beat, HEARTBEAT_INTERVAL_MS);
    document.addEventListener('visibilitychange', beat);
    const off = onArmedChange(() => beat());
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', beat);
      off();
    };
  }, [active, queryClient]);
}
